import t from "@ext/localization/locale/translate";
import { useFilePreviewModalContext } from "@ext/markdown/elements/file/edit/components/Preview/FilePreviewModalContext";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuTrigger,
} from "@ui-kit/Dropdown";
import {
	GlassToolbar,
	GlassToolbarButton,
	GlassToolbarIcon,
	GlassToolbarSeparator,
	GlassToolbarText,
	GlassToolbarTrigger,
} from "@ui-kit/GlassToolbar";
import { TextOverflowTooltip } from "@ui-kit/Tooltip";
import type { ReactElement } from "react";

const dropdownContentClassName = "max-h-[min(360px,50vh)] max-w-[min(320px,calc(100vw-32px))] overflow-y-auto";

const PageNavigation = (): ReactElement => {
	const { activePage, goToNextPage, goToPreviousPage, goToSelectedPage, lastPage, pageItems } =
		useFilePreviewModalContext();

	return (
		<>
			<GlassToolbarButton
				disabled={activePage <= 1}
				onClick={goToPreviousPage}
				tooltipText={t("pagination.previous")}
			>
				<GlassToolbarIcon icon="chevron-left" />
			</GlassToolbarButton>
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<GlassToolbarTrigger aria-label={t("file-preview.page")}>
						<GlassToolbarText className="whitespace-nowrap px-1 text-xs">
							{`${activePage} / ${lastPage}`}
						</GlassToolbarText>
					</GlassToolbarTrigger>
				</DropdownMenuTrigger>
				<DropdownMenuContent className={dropdownContentClassName} side="top" sideOffset={8}>
					<DropdownMenuRadioGroup onValueChange={goToSelectedPage} value={String(activePage)}>
						{pageItems.map((page) => (
							<DropdownMenuRadioItem key={page} value={String(page)}>
								{t("file-preview.page")} {page} {t("file-preview.of")} {lastPage}
							</DropdownMenuRadioItem>
						))}
					</DropdownMenuRadioGroup>
				</DropdownMenuContent>
			</DropdownMenu>
			<GlassToolbarButton
				disabled={activePage >= lastPage}
				onClick={goToNextPage}
				tooltipText={t("pagination.next")}
			>
				<GlassToolbarIcon icon="chevron-right" />
			</GlassToolbarButton>
		</>
	);
};

const SheetSelect = (): ReactElement => {
	const { meta, selectSheet, selectedSheetName, sheetItems } = useFilePreviewModalContext();
	const currentSheetName = selectedSheetName ?? meta.sheetName;

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<GlassToolbarTrigger aria-label={t("file-preview.sheet")} className="min-w-0 max-w-[240px]">
					<TextOverflowTooltip className="min-w-0 px-1 text-xs">{currentSheetName}</TextOverflowTooltip>
					<GlassToolbarIcon icon="chevron-down" />
				</GlassToolbarTrigger>
			</DropdownMenuTrigger>
			<DropdownMenuContent className={dropdownContentClassName} side="top" sideOffset={8}>
				<DropdownMenuRadioGroup onValueChange={selectSheet} value={currentSheetName}>
					{sheetItems.map((sheetName) => (
						<DropdownMenuRadioItem key={sheetName} value={sheetName}>
							<TextOverflowTooltip className="min-w-0">{sheetName}</TextOverflowTooltip>
						</DropdownMenuRadioItem>
					))}
				</DropdownMenuRadioGroup>
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

const ZoomControls = (): ReactElement => {
	const { maxZoom, minZoom, zoom, zoomIn, zoomOut } = useFilePreviewModalContext();

	return (
		<>
			<GlassToolbarButton disabled={zoom <= minZoom} onClick={zoomOut} tooltipText={t("file-preview.zoom-out")}>
				<GlassToolbarIcon icon="zoom-out" />
			</GlassToolbarButton>
			<GlassToolbarText className="min-w-[44px] justify-center whitespace-nowrap text-xs">
				{`${Math.round(zoom * 100)}%`}
			</GlassToolbarText>
			<GlassToolbarButton disabled={zoom >= maxZoom} onClick={zoomIn} tooltipText={t("file-preview.zoom-in")}>
				<GlassToolbarIcon icon="zoom-in" />
			</GlassToolbarButton>
		</>
	);
};

export const FilePreviewModalToolbar = (): ReactElement => {
	const { hasPageNavigation, hasSheetNavigation } = useFilePreviewModalContext();
	const navigation = hasPageNavigation ? <PageNavigation /> : hasSheetNavigation ? <SheetSelect /> : null;

	return (
		<GlassToolbar
			aria-label={t("file-preview.title")}
			className="absolute bottom-4 left-1/2 z-10 w-fit max-w-[calc(100%-32px)] -translate-x-1/2"
		>
			{navigation}
			{navigation && <GlassToolbarSeparator />}
			<ZoomControls />
		</GlassToolbar>
	);
};
